import React from 'react';
import ReportShell, { reportStyle, reportButtonStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import { formatMoney, getDerivedRrp } from './productReportShared';
import { AvailabilityPill } from '../../../shared/Pill';
import { downloadCsv } from '../../../shared/csv';
import { formatMoney as formatMoneyShared } from '../../../shared/numberFormat';
import TaxBasisLine from '../../../shared/TaxBasisLine';

// Customer-facing badge, same pill as the Products list's optional
// Availability Badge column.
function renderAvailabilityBadges(product) {
  return (
    <AvailabilityPill
      badge={product.availability_badge}
      date={product.availability_date}
      color={product.availability_badge_color}
    />
  );
}

function buildSummaryCsvRows(products, trackRrp, defaultTaxRate, anchorName) {
  return products.map((product) => {
    const hasSupplierCost = product.supplier && product.cost_price_foreign !== null && product.cost_price_foreign !== undefined;
    const costAud = hasSupplierCost
      ? Number(product.cost_price_foreign) * Number(product.supplier.exchange_rate)
      : product.cost_price;
    const rrp = getDerivedRrp(product, trackRrp, defaultTaxRate, anchorName);

    return {
      SKU: product.sku,
      Name: product.name,
      Category: product.product_category?.name || '',
      'Product Type': product.product_type?.code || '',
      'Cost Currency': hasSupplierCost ? product.supplier.currency_code : 'AUD',
      'Cost (Foreign)': hasSupplierCost ? Number(product.cost_price_foreign).toFixed(2) : '',
      'Cost (AUD)': costAud !== null && costAud !== undefined ? Number(costAud).toFixed(2) : '',
      [`${rrp.heading} ${rrp.gstSuffix}`]: rrp.value !== null && rrp.value !== undefined ? Number(rrp.value).toFixed(2) : '',
    };
  });
}

// Mirrors ProductTable.js's renderCostPrice — foreign cost on top (when the
// product has a supplier), converted home-currency amount underneath in grey, so a product's
// Cost reads the same way here as it does on the list it was printed from.
function renderCost(product) {
  if (product.supplier && product.cost_price_foreign !== null && product.cost_price_foreign !== undefined) {
    const foreign = Number(product.cost_price_foreign);
    const aud = foreign * Number(product.supplier.exchange_rate);
    return (
      <>
        <div style={{ whiteSpace: 'nowrap' }}>{formatMoneyShared(foreign, { currency: product.supplier.currency_code })}</div>
        <div style={{ color: '#888', fontSize: 12, whiteSpace: 'nowrap' }}>{formatMoney(aud)}</div>
      </>
    );
  }
  return formatMoney(product.cost_price);
}

// One-line-per-product print/CSV report for the Product List screen (story #78).
function ProductSummaryReportView({ products, loading, filterSummary, trackRrp, defaultTaxRate, anchorName, onClose }) {
  const isEmpty = !loading && products.length === 0;
  const rrpHeading = anchorName;
  const rrpGstSuffix = trackRrp ? 'inc GST' : 'ex GST';

  return (
    <ReportShell
      title="Product Summary Report"
      subtitle={(
        <>
          <div>{`${formatReportTimestamp()} · ${products.length} product${products.length === 1 ? '' : 's'}`}</div>
          {filterSummary && <div>Filtered by: {filterSummary}</div>}
        </>
      )}
      onClose={onClose}
      printDisabled={loading || isEmpty}
      actions={(
        <button
          type="button"
          onClick={() => downloadCsv('product-summary-report.csv', buildSummaryCsvRows(products, trackRrp, defaultTaxRate, anchorName))}
          disabled={loading || isEmpty}
          style={reportButtonStyle(loading || isEmpty)}
        >
          Download CSV
        </button>
      )}
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading...</div>
      ) : isEmpty ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>No products match the current filters.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: reportStyle.catalogFontSize }}>
          <thead>
            {/* Print-only spacer: the fixed running-header (company name +
                title, see ReportShell/App.css's .print-running-header)
                repeats on every physical page, so without its own reserved
                clearance the repeated column-header row below lands right
                against it on continuation pages — see the App.css comment
                on this same pattern in the Pricelist reports. */}
            <tr className="print-only-row"><td colSpan={6} style={{ height: 18, padding: 0, border: 'none' }} /></tr>
            <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
              <th style={{ padding: '6px 8px 6px 0' }}>SKU</th>
              <th style={{ padding: '6px 8px 6px 0' }}>Name</th>
              <th style={{ padding: '6px 8px 6px 0' }}>Category</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'center', lineHeight: 1.3 }}>
                <div>Product</div>
                <div>Type</div>
              </th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', lineHeight: 1.3, whiteSpace: 'nowrap' }}>Cost</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                <div>{rrpHeading}</div>
                <TaxBasisLine>{rrpGstSuffix}</TaxBasisLine>
              </th>
            </tr>
          </thead>
          {/* Same idea in reverse: the fixed running-footer repeats too, so
              a blank spacer here (the browser repeats <tfoot> per page, same
              as <thead>) stops the last row on each page from packing right
              up against it. */}
          <tfoot>
            <tr className="print-only-row"><td colSpan={6} style={{ height: 20, padding: 0, border: 'none' }} /></tr>
          </tfoot>
          <tbody>
            {products.map((product) => (
              <tr key={product.id} style={{ borderBottom: '1px solid #ddd', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                <td style={{ padding: '6px 8px 6px 0', fontWeight: 600 }}>{product.sku}</td>
                <td style={{ padding: '6px 8px 6px 0' }}>{product.name}{' '}{renderAvailabilityBadges(product)}</td>
                <td style={{ padding: '6px 8px 6px 0' }}>{product.product_category?.name || ''}</td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>{product.product_type?.code || ''}</td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', lineHeight: 1.3 }}>{renderCost(product)}</td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{formatMoney(getDerivedRrp(product, trackRrp, defaultTaxRate, anchorName).value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ReportShell>
  );
}

export default ProductSummaryReportView;
